import React from 'react';
import { Target, Eye, Heart, Users, Award } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { aboutData, partnershipsData } from '../data/mock';
import { useEffect } from 'react';

const About = () => {
  useEffect(() => {
    document.title = "About Us - David's Harmony House Foundation Inc.";
  }, []);

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative py-24 bg-gradient-to-br from-orange-50 via-amber-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              About <span className="text-orange-600">Our Foundation</span>
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              Building a faith-based continuum of care ecosystem that transforms lives 
              through sustainable pathways of restoration and economic empowerment.
            </p>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
              <div>
                <h2 className="text-4xl font-bold text-gray-900 mb-6">Our Story</h2>
                <p className="text-lg text-gray-600 leading-relaxed mb-6">
                  {aboutData.story}
                </p>
                <div className="bg-orange-50 border-l-4 border-orange-600 p-6 rounded-r-lg">
                  <p className="text-gray-700 leading-relaxed">
                    <strong className="text-orange-600">Our Approach:</strong> We don't just provide 
                    services—we create comprehensive support ecosystems that address the root causes 
                    of instability and provide sustainable pathways to independence.
                  </p>
                </div>
              </div>
              <div className="relative">
                <img loading="lazy"
                  src="https://images.pexels.com/photos/6647015/pexels-photo-6647015.jpeg"
                  alt="Community volunteers"
                  className="rounded-2xl shadow-2xl"
                />
                <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-xl shadow-xl border-2 border-orange-200">
                  <div className="text-4xl font-bold text-orange-600 mb-1">500+</div>
                  <div className="text-sm text-gray-600 font-medium">Lives Transformed</div>
                </div>
              </div>
            </div>

            {/* Mission, Vision, Values */}
            <div className="grid md:grid-cols-3 gap-8 mb-16">
              <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow duration-300">
                <CardContent className="p-8">
                  <div className="w-14 h-14 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center mb-6">
                    <Target className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">Our Mission</h3>
                  <p className="text-gray-600 leading-relaxed">
                    {aboutData.mission}
                  </p>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow duration-300">
                <CardContent className="p-8">
                  <div className="w-14 h-14 bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl flex items-center justify-center mb-6">
                    <Eye className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">Our Vision</h3>
                  <p className="text-gray-600 leading-relaxed">
                    {aboutData.vision}
                  </p>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow duration-300">
                <CardContent className="p-8">
                  <div className="w-14 h-14 bg-gradient-to-br from-orange-600 to-red-600 rounded-xl flex items-center justify-center mb-6">
                    <Heart className="w-7 h-7 text-white" fill="white" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">Our Values</h3>
                  <ul className="text-gray-600 space-y-2">
                    <li className="flex items-start">
                      <span className="text-orange-600 mr-2">•</span>
                      <span>Dignity & Respect</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-orange-600 mr-2">•</span>
                      <span>Faith-Based Care</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-orange-600 mr-2">•</span>
                      <span>Community Impact</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-orange-600 mr-2">•</span>
                      <span>Sustainable Change</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Leadership */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Leadership</h2>
              <p className="text-xl text-gray-600">
                Guided by vision, driven by compassion
              </p>
            </div>

            <Card className="border-0 shadow-xl">
              <CardContent className="p-8">
                <div className="flex flex-col md:flex-row gap-8 items-center">
                  {aboutData.founder.image ? (
                    <div className="w-40 h-40 flex-shrink-0">
                      <img loading="lazy"
                        src={aboutData.founder.image}
                        alt={aboutData.founder.name}
                        className="w-full h-full object-cover rounded-full shadow-lg border-4 border-orange-100"
                      />
                    </div>
                  ) : (
                    <div className="w-32 h-32 bg-gradient-to-br from-orange-500 to-orange-600 rounded-full flex items-center justify-center flex-shrink-0">
                      <Users className="w-16 h-16 text-white" />
                    </div>
                  )}
                  <div className="flex-1 text-center md:text-left">
                    <h3 className="text-3xl font-bold text-gray-900 mb-2">
                      {aboutData.founder.name}
                    </h3>
                    <p className="text-orange-600 font-semibold mb-4">
                      {aboutData.founder.title}
                    </p>
                    <p className="text-gray-600 leading-relaxed">
                      {aboutData.founder.bio}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Partnerships */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Our <span className="text-orange-600">Strategic Partnerships</span>
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Collaboration is at the heart of our impact. We work alongside government agencies, 
                community organizations, and faith-based partners to create systemic change.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {partnershipsData.map((partner, index) => (
                <Card 
                  key={index} 
                  className="border-2 border-gray-200 hover:border-orange-400 hover:shadow-lg transition-all duration-300"
                >
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Award className="w-6 h-6 text-orange-600" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-bold text-gray-900 mb-1">
                          {partner.name}
                        </h3>
                        <p className="text-sm text-orange-600 font-medium mb-3">
                          {partner.type}
                        </p>
                        <p className="text-gray-600 text-sm leading-relaxed">
                          {partner.description}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-gradient-to-br from-orange-600 to-amber-600 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Join Us in Making a Difference
            </h2>
            <p className="text-xl mb-8 text-orange-100">
              Whether through donations, volunteering, or partnerships, your support 
              helps us transform lives and build stronger communities.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="/donate">
                <button className="px-8 py-4 bg-white text-orange-600 rounded-lg font-semibold hover:bg-gray-100 transition-colors duration-300 shadow-xl">
                  Donate Now
                </button>
              </a>
              <a href="/get-involved">
                <button className="px-8 py-4 border-2 border-white text-white rounded-lg font-semibold hover:bg-white hover:text-orange-600 transition-all duration-300">
                  Get Involved
                </button>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
